import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Newspaper, EnvelopeSimple, ArrowsClockwise, Plus, ArrowUpRight, Eye, UsersThree, Buildings, Globe, Detective, TrendUp, Flower, Warning, WhatsappLogo, ShieldWarning, Prohibit, MagnifyingGlass, PencilSimple, Megaphone, CheckCircle, UserCircle } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminContent() {
  useSeo("Admin", null, null, { noindex: true });
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const isStaff = isAdmin || user?.role === "moderator";
  useEffect(() => { if (user && !isStaff) nav("/dashboard"); }, [user, isStaff, nav]);

  return (
    <main className="mx-auto max-w-5xl px-5 py-8" data-testid="admin-content">
      <div className="flex items-center gap-2">
        <h1 className="font-heading text-3xl font-medium">Admin dashboard</h1>
        {!isAdmin && <Badge className="rounded-full bg-secondary/15 font-normal text-secondary" data-testid="moderator-badge">Moderator</Badge>}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{isAdmin ? "Your platform at a glance — plus publishing tools." : "Forum moderation & member management."}</p>
      {isAdmin && <Analytics />}
      <Tabs defaultValue={isAdmin ? "articles" : "moderation"} className="mt-8">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="moderation" data-testid="tab-moderation"><ShieldWarning size={15} className="mr-1.5" /> Moderation</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><UsersThree size={15} className="mr-1.5" /> Users</TabsTrigger>
          {isAdmin && <>
            <TabsTrigger value="edit-inst" data-testid="tab-edit-inst"><PencilSimple size={15} className="mr-1.5" /> Edit institutions</TabsTrigger>
            <TabsTrigger value="articles" data-testid="tab-articles"><FileText size={15} className="mr-1.5" /> Articles</TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news"><Newspaper size={15} className="mr-1.5" /> News</TabsTrigger>
            <TabsTrigger value="contact" data-testid="tab-contact"><EnvelopeSimple size={15} className="mr-1.5" /> Messages</TabsTrigger>
            <TabsTrigger value="memorials" data-testid="tab-memorials"><Flower size={15} className="mr-1.5" /> Memorials</TabsTrigger>
            <TabsTrigger value="suggestions" data-testid="tab-suggestions"><Buildings size={15} className="mr-1.5" /> Institutions</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests"><Megaphone size={15} className="mr-1.5" /> Requests</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp"><WhatsappLogo size={15} className="mr-1.5" /> WhatsApp</TabsTrigger>
            <TabsTrigger value="duplicates" data-testid="tab-duplicates"><Detective size={15} className="mr-1.5" /> Duplicates</TabsTrigger>
          </>}
        </TabsList>
        <TabsContent value="moderation" className="mt-5"><ModerationTab /></TabsContent>
        <TabsContent value="users" className="mt-5"><UsersTab isAdmin={isAdmin} selfId={user?.id} /></TabsContent>
        {isAdmin && <>
          <TabsContent value="edit-inst" className="mt-5"><InstitutionEditTab /></TabsContent>
          <TabsContent value="articles" className="mt-5"><ArticlesTab /></TabsContent>
          <TabsContent value="news" className="mt-5"><NewsTab /></TabsContent>
          <TabsContent value="contact" className="mt-5"><ContactTab /></TabsContent>
          <TabsContent value="memorials" className="mt-5"><MemorialsTab /></TabsContent>
          <TabsContent value="suggestions" className="mt-5"><InstitutionSuggestionsTab /></TabsContent>
          <TabsContent value="requests" className="mt-5"><InstitutionRequestsTab /></TabsContent>
          <TabsContent value="whatsapp" className="mt-5"><WhatsAppTab /></TabsContent>
          <TabsContent value="duplicates" className="mt-5"><DuplicatesTab /></TabsContent>
        </>}
      </Tabs>
    </main>
  );
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <Icon size={18} weight="duotone" className="text-secondary" />
      </div>
      <div className="mt-2 font-heading text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => setA(false)); }, []);
  if (!a) return <div className="mt-6 h-24 animate-pulse rounded-2xl bg-muted/40" data-testid="admin-analytics-loading" />;
  const k = a.kpis;
  const maxTrend = Math.max(1, ...a.signups_trend.map((d) => d.count));
  return (
    <div className="mt-6" data-testid="admin-analytics">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <Kpi icon={Eye} label="Website visits" value={k.site_views} sub="across institution sites" testid="kpi-visits" />
        <Kpi icon={UsersThree} label="Members" value={k.users_total} sub={`+${k.users_7} this week`} testid="kpi-users" />
        <Kpi icon={Buildings} label="Institutions" value={k.institutions} sub={`${k.published_sites} published`} testid="kpi-institutions" />
        <Kpi icon={Detective} label="Reconnect posts" value={k.find_open} sub={`${k.find_total} all-time`} testid="kpi-finds" />
        <Kpi icon={Globe} label="Alumni groups" value={k.groups} testid="kpi-groups" />
        <Kpi icon={EnvelopeSimple} label="Messages" value={k.contact_total} sub={`${k.contact_new} unread`} testid="kpi-contact" />
        <Kpi icon={Newspaper} label="News items" value={k.news} testid="kpi-news" />
        <Kpi icon={FileText} label="Articles" value={k.articles} testid="kpi-articles" />
      </div>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2"><TrendUp size={18} weight="duotone" className="text-secondary" /><span className="font-medium">New members · last 7 days</span></div>
          <div className="flex h-28 items-end gap-2">
            {a.signups_trend.map((d) => (
              <div key={d.day} className="flex flex-1 flex-col items-center gap-1">
                <div className="w-full rounded-t bg-secondary/70" style={{ height: `${(d.count / maxTrend) * 90 + 4}px` }} title={`${d.count}`} />
                <span className="text-[10px] text-muted-foreground">{d.day}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2"><Buildings size={18} weight="duotone" className="text-secondary" /><span className="font-medium">Top institutions</span></div>
          <div className="space-y-2">
            {a.top_institutions.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate">{i.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{i.alumni_count} alumni · {i.views} views</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ArticlesTab() {
  const [form, setForm] = useState({ title: "", summary: "", body: "", cover_url: "", tags: "" });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/articles").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.title || !form.body) return toast.error("Title and body are required");
    setBusy(true);
    try {
      await api.post("/articles", { ...form, tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Article published"); setForm({ title: "", summary: "", body: "", cover_url: "", tags: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
      <Card className="border-border/60 p-5">
        <div className="space-y-3">
          <div><Label>Title</Label><Input className="mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="article-title" /></div>
          <div><Label>Summary</Label><Input className="mt-1" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="article-summary" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Cover image URL</Label><Input className="mt-1" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} placeholder="https://" /></div>
            <div><Label>Tags (comma separated)</Label><Input className="mt-1" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Guides, Community" /></div>
          </div>
          <div><Label>Body</Label><Textarea rows={6} className="mt-1" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Write your article… (use blank lines between paragraphs)" data-testid="article-body" /></div>
          <Button onClick={submit} disabled={busy} className="rounded-full gap-1.5" data-testid="article-publish"><Plus size={15} /> {busy ? "Publishing…" : "Publish article"}</Button>
        </div>
      </Card>
      <div>
        <p className="mb-2 text-sm font-medium text-muted-foreground">Published articles ({items.length})</p>
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl border border-border/60 p-3">
              <div className="min-w-0"><span className="block truncate font-medium">{a.title}</span><span className="text-xs text-muted-foreground">{(a.tags || []).join(", ")}</span></div>
              <a href={`/articles/${a.slug}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-secondary"><ArrowUpRight size={16} /></a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NewsTab() {
  const [form, setForm] = useState({ title: "", summary: "", url: "", source: "AlumniPulse", tags: "" });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [genCountry, setGenCountry] = useState("");
  const [generating, setGenerating] = useState(false);
  const load = () => api.get("/news").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.title || !form.url) return toast.error("Title and URL are required");
    setBusy(true);
    try {
      await api.post("/news", { ...form, tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("News item added"); setForm({ title: "", summary: "", url: "", source: "AlumniPulse", tags: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const generate = async () => {
    if (!genCountry.trim()) return toast.error("Enter a country");
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/news/generate", { country: genCountry.trim() });
      toast.success(`Fetched ${data.added} new headline${data.added !== 1 ? "s" : ""} for ${data.country}`);
      setGenCountry(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try { const { data } = await api.post("/news/refresh"); toast.success(`Fetched ${data.added} new items`); load(); }
    catch (e) { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };
  return (
    <div className="space-y-6">
      <Card className="border-secondary/30 bg-secondary/5 p-5" data-testid="news-generate-card">
        <div className="flex items-center gap-2"><Globe size={18} weight="duotone" className="text-secondary" /><span className="font-medium">Collect news by country</span></div>
        <p className="mt-1 text-sm text-muted-foreground">Pull real, localised education & alumni headlines for any country worldwide. They appear on that country's public page automatically.</p>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <Input value={genCountry} onChange={(e) => setGenCountry(e.target.value)} placeholder="e.g. Germany, Japan, Nigeria" className="rounded-full sm:flex-1" data-testid="news-generate-country" />
          <Button onClick={generate} disabled={generating} className="rounded-full gap-1.5" data-testid="news-generate-submit"><Globe size={15} /> {generating ? "Collecting…" : "Collect news"}</Button>
        </div>
      </Card>
      <Card className="border-border/60 p-5">
        <div className="mb-4 flex items-center justify-between">
          <span className="font-medium">Add an editorial item</span>
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={refresh} disabled={refreshing} data-testid="news-refresh"><ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Fetching…" : "Refresh feed"}</Button>
        </div>
        <div className="space-y-3">
          <div><Label>Headline</Label><Input className="mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-title" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Link URL</Label><Input className="mt-1" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://" data-testid="news-url" /></div>
            <div><Label>Source</Label><Input className="mt-1" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
          </div>
          <div><Label>Tags (comma separated)</Label><Input className="mt-1" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Product, Community" /></div>
          <Button onClick={submit} disabled={busy} className="rounded-full gap-1.5" data-testid="news-publish"><Plus size={15} /> {busy ? "Adding…" : "Add news"}</Button>
        </div>
      </Card>
      <div>
        <p className="mb-2 text-sm font-medium text-muted-foreground">Latest news ({items.length})</p>
        <div className="space-y-2">
          {items.slice(0, 15).map((n) => (
            <div key={n.id} className="flex items-center justify-between gap-3 rounded-xl border border-border/60 p-3">
              <div className="min-w-0"><span className="block truncate font-medium">{n.title}</span><span className="text-xs text-muted-foreground">{n.source}{n.country ? ` · ${n.country}` : ""}</span></div>
              <a href={n.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-secondary"><ArrowUpRight size={16} /></a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ContactTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/contact").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (items === null) return <p className="text-muted-foreground">Loading…</p>;
  if (items.length === 0) return <p className="text-muted-foreground">No messages yet.</p>;
  return (
    <div className="space-y-3" data-testid="contact-messages">
      {items.map((m) => (
        <Card key={m.id} className="border-border/60 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium">{m.name} <span className="text-muted-foreground">·</span> <a href={`mailto:${m.email}`} className="text-secondary hover:underline">{m.email}</a></span>
            <Badge variant="outline" className="rounded-full text-xs">{new Date(m.created_at).toLocaleDateString()}</Badge>
          </div>
          {m.subject && <p className="mt-1 text-sm font-medium">{m.subject}</p>}
          <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{m.message}</p>
        </Card>
      ))}
    </div>
  );
}

const VERIF_OPTS = [
  ["submitted", "Submitted"], ["community_suggested", "Community Suggested"], ["reviewed", "Reviewed"],
  ["institution_verified", "Institution Verified"], ["family_verified", "Family Verified"],
];

function MemorialRow({ m, onChange }) {
  const [busy, setBusy] = useState(false);
  const act = async (payload) => {
    setBusy(true);
    try { await api.post(`/admin/memorials/${m.id}/review`, payload); toast.success("Updated"); onChange(); }
    catch { toast.error("Action failed"); } finally { setBusy(false); }
  };
  const reports = (m.reports || []).length;
  const flags = m.flags || {};
  const livingFlag = (flags.living_person_conflict || []).length > 0 || flags.reported_living;
  const dupeFlag = (flags.duplicate_risk || []).length > 0;
  return (
    <Card className="border-border/60 p-4" data-testid={`admin-memorial-${m.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="font-medium">{m.name}</span>
          <span className="ml-2 text-xs text-muted-foreground">{[m.class_batch, m.institution].filter(Boolean).join(" · ")}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className="rounded-full text-xs">{m.record_type}</Badge>
          <Badge variant="outline" className="rounded-full text-xs">{m.state}</Badge>
          {(m.ai_generated || m.ai_assisted) && <Badge className="rounded-full bg-violet-100 text-xs text-violet-800">AI</Badge>}
          {reports > 0 && <Badge className="rounded-full bg-red-100 text-xs text-red-800">{reports} report{reports > 1 ? "s" : ""}</Badge>}
        </div>
      </div>
      {(livingFlag || dupeFlag) && (
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {livingFlag && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-800"><Warning size={12} /> Living-person conflict</span>}
          {dupeFlag && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800"><Warning size={12} /> Possible duplicate</span>}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <a href={`/memorials/${m.id}`} target="_blank" rel="noreferrer" className="text-xs text-secondary hover:underline">Preview</a>
        <Select onValueChange={(v) => act({ verification_status: v })}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid={`verif-select-${m.id}`}><SelectValue placeholder="Set verification…" /></SelectTrigger>
          <SelectContent>{VERIF_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {m.state !== "published" && <Button size="sm" className="h-8 rounded-full text-xs" disabled={busy} onClick={() => act({ state: "published" })} data-testid={`publish-${m.id}`}>Publish</Button>}
        {m.state !== "rejected" && <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" disabled={busy} onClick={() => act({ state: "rejected" })} data-testid={`reject-${m.id}`}>Reject</Button>}
        {m.state !== "archived" && <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs text-muted-foreground" disabled={busy} onClick={() => act({ state: "archived" })} data-testid={`archive-${m.id}`}>Archive</Button>}
      </div>
    </Card>
  );
}

function MemorialsTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending_review");
  const load = () => api.get(`/admin/memorials${filter ? `?state=${filter}` : ""}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [filter]);
  return (
    <div className="space-y-4" data-testid="admin-memorials-tab">
      <div className="flex flex-wrap gap-2">
        {[["pending_review", "Pending review"], ["published", "Published"], ["rejected", "Rejected"], ["archived", "Archived"], ["", "All"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`admin-mem-filter-${v || "all"}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === v ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{l}</button>
        ))}
      </div>
      {items === null ? <p className="text-muted-foreground">Loading…</p>
        : items.length === 0 ? <p className="text-muted-foreground">No memorials in this view.</p>
          : <div className="space-y-3">{items.map((m) => <MemorialRow key={m.id} m={m} onChange={load} />)}</div>}
    </div>
  );
}


const SUG_STATUSES = [
  ["submitted", "Submitted"], ["researching", "Researching"], ["information_needed", "Info needed"],
  ["under_review", "Under review"], ["approved", "Approved"], ["added", "Added"],
  ["duplicate", "Duplicate"], ["not_verified", "Not verified"], ["archived", "Archived"],
];

function SuggestionRow({ s, onChange }) {
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const r = s.research;

  const act = async (fn, key) => {
    setBusy(key);
    try { await fn(); toast.success("Updated"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };
  const research = () => act(() => api.post(`/admin/institution-suggestions/${s.id}/research`), "research");
  const approve = () => act(() => api.post(`/admin/institution-suggestions/${s.id}/approve`), "approve");
  const setStatus = (status, n = "") => act(() => api.post(`/admin/institution-suggestions/${s.id}/status`, { status, note: n }), status);

  const links = [["Website", s.website], ["Facebook", s.facebook], ["LinkedIn", s.linkedin], ["Wikipedia", s.wikipedia], ["Other", s.other_links]].filter(([, v]) => v);

  return (
    <Card className="border-border/60 p-4" data-testid={`admin-suggestion-${s.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-lg font-medium">{s.name}</h3>
            <Badge className="rounded-full font-normal">{(SUG_STATUSES.find(([v]) => v === s.status) || [, s.status])[1]}</Badge>
            {s.credit_opt_in && <Badge variant="outline" className="rounded-full text-xs font-normal">Credit OK</Badge>}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{[s.type, s.city || s.locality, s.state, s.country].filter(Boolean).join(" · ")}</p>
          <p className="mt-1 text-xs text-muted-foreground">By {s.submitter_name || "—"} ({s.submitter_email || "no email"}){s.source_relationship ? ` · ${s.source_relationship}` : ""}</p>
        </div>
      </div>

      {(s.other_names || s.former_name || s.founded || s.history || s.extra || s.head) && (
        <div className="mt-2 space-y-1 text-sm">
          {s.other_names && <p><span className="text-muted-foreground">Also known as:</span> {s.other_names}</p>}
          {s.former_name && <p><span className="text-muted-foreground">Former:</span> {s.former_name}{s.new_name ? ` → ${s.new_name}` : ""}</p>}
          {s.founded && <p><span className="text-muted-foreground">Founded:</span> {s.founded}{s.operating_status ? ` · ${s.operating_status}${s.closure_status ? " (" + s.closure_status + ")" : ""}` : ""}</p>}
          {s.head && <p><span className="text-muted-foreground">Head:</span> {s.head}</p>}
          {s.history && <p className="text-muted-foreground">{s.history}</p>}
          {s.extra && <p className="text-muted-foreground italic">{s.extra}</p>}
        </div>
      )}

      {links.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-3 text-xs">
          {links.map(([l, u]) => <a key={l} href={u} target="_blank" rel="noreferrer" className="text-secondary hover:underline">{l} ↗</a>)}
        </div>
      )}

      {s.duplicate_matches?.length > 0 && (
        <div className="mt-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
          <strong>Possible duplicates:</strong> {s.duplicate_matches.map((d) => d.name).join(", ")}
        </div>
      )}

      {r && (
        <div className="mt-3 rounded-xl border border-secondary/20 bg-secondary/5 p-3 text-sm" data-testid="suggestion-research">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="rounded-full bg-secondary/15 font-normal text-secondary">AI research</Badge>
            <span className="text-xs text-muted-foreground">Verification: <strong>{r.verification}</strong> · Confidence: <strong>{r.confidence}</strong></span>
          </div>
          <p className="mt-1.5 text-muted-foreground">{r.summary}</p>
          {r.sources?.length > 0 && <p className="mt-1 text-xs text-muted-foreground">Sources: {r.sources.join(", ")}</p>}
          {r.duplicate_risk && <p className="mt-1 text-xs text-muted-foreground">Duplicate risk: {r.duplicate_risk}</p>}
          {r.recommendation && <p className="mt-1 text-xs font-medium">Recommendation: {r.recommendation}</p>}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={research} disabled={busy === "research"} data-testid={`research-${s.id}`}>{busy === "research" ? "Researching…" : (r ? "Re-run AI research" : "AI research")}</Button>
        {!s.institution_id && <Button size="sm" className="rounded-full" onClick={approve} disabled={busy === "approve"} data-testid={`approve-${s.id}`}>{busy === "approve" ? "Adding…" : "Approve & create"}</Button>}
        {s.institution_id && <a href={`/institutions/${s.institution_id}`} target="_blank" rel="noreferrer" className="text-xs text-secondary hover:underline">View institution ↗</a>}
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => setShowNote((v) => !v)}>Set status</Button>
      </div>

      {showNote && (
        <div className="mt-2 space-y-2 rounded-xl bg-muted/30 p-3">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to the submitter (e.g. what info is needed)" className="text-sm" data-testid={`note-${s.id}`} />
          <div className="flex flex-wrap gap-1.5">
            {SUG_STATUSES.map(([v, l]) => (
              <button key={v} onClick={() => setStatus(v, note)} disabled={!!busy}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${s.status === v ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{l}</button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function InstitutionSuggestionsTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => { setItems(null); api.get("/admin/institution-suggestions", { params: { status: filter } }).then((r) => setItems(r.data)).catch(() => setItems([])); };
  useEffect(load, [filter]);
  return (
    <div className="space-y-6" data-testid="suggestions-tab">
      <CorrectionsPanel />
      <div>
        <h3 className="mb-2 font-heading text-lg font-medium">Suggested institutions</h3>
        <div className="flex flex-wrap gap-2">
          {[["", "All"], ...SUG_STATUSES].map(([v, l]) => (
            <button key={v || "all"} onClick={() => setFilter(v)} data-testid={`sug-filter-${v || "all"}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === v ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{l}</button>
          ))}
        </div>
        {items === null ? <p className="mt-3 text-muted-foreground">Loading…</p>
          : items.length === 0 ? <p className="mt-3 text-muted-foreground">No institution suggestions in this view.</p>
            : <div className="mt-3 space-y-3">{items.map((s) => <SuggestionRow key={s.id} s={s} onChange={load} />)}</div>}
      </div>
    </div>
  );
}

const REQ_STATUSES = [["new", "New"], ["reviewed", "Reviewed"], ["verified", "Verified"], ["rejected", "Rejected"]];

function InstitutionRequestsTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => { setData(null); api.get("/admin/institution-requests", { params: { status: filter } }).then((r) => setData(r.data)).catch(() => setData({ items: [], new: 0 })); };
  useEffect(load, [filter]);
  const setStatus = async (rid, status) => {
    try { await api.patch(`/admin/institution-requests/${rid}`, null, { params: { status } }); toast.success("Updated"); load(); }
    catch { toast.error("Failed to update"); }
  };
  return (
    <div className="space-y-4" data-testid="requests-tab">
      <div>
        <h3 className="mb-1 font-heading text-lg font-medium">Institution requests (CRM)</h3>
        <p className="mb-3 text-sm text-muted-foreground">Visitor-submitted institutions (auto-created, unverified). Reach out, verify the details, then mark verified.</p>
        <div className="flex flex-wrap gap-2">
          {[["", "All"], ...REQ_STATUSES].map(([v, l]) => (
            <button key={v || "all"} onClick={() => setFilter(v)} data-testid={`req-filter-${v || "all"}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === v ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{l}</button>
          ))}
        </div>
      </div>
      {data === null ? <p className="text-muted-foreground">Loading…</p>
        : data.items.length === 0 ? <p className="text-muted-foreground">No requests in this view.</p>
        : (
          <div className="space-y-3">
            {data.items.map((r) => {
              const waDigits = (r.whatsapp || "").replace(/\D/g, "");
              const loc = [r.city_village, r.district, r.state].filter(Boolean).join(", ");
              return (
                <Card key={r.id} className="border-border/60 p-4" data-testid={`request-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        {r.institution_id
                          ? <a href={`/institutions/${r.institution_id}`} target="_blank" rel="noreferrer" className="font-medium text-secondary hover:underline" data-testid={`request-inst-link-${r.id}`}>{r.institution_name}</a>
                          : <span className="font-medium">{r.institution_name}</span>}
                        <Badge variant="outline" className="rounded-full text-xs font-normal capitalize" data-testid={`request-status-badge-${r.id}`}>{r.status}</Badge>
                      </div>
                      {loc && <p className="mt-1 text-sm text-muted-foreground">{loc}</p>}
                      {r.address && <p className="text-xs text-muted-foreground">{r.address}</p>}
                      {r.notes && <p className="mt-1 text-sm">{r.notes}</p>}
                      <p className="mt-2 text-xs text-muted-foreground">
                        {r.full_name}{r.gender ? ` · ${r.gender}` : ""}
                        {" · "}
                        <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noreferrer" className="font-medium text-green-600 hover:underline" data-testid={`request-whatsapp-${r.id}`}>WhatsApp {r.whatsapp}</a>
                        {r.email ? <> · <a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a></> : ""}
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString() : ""} · {r.edition || "india"}</p>
                    </div>
                    <div className="w-40 shrink-0">
                      <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                        <SelectTrigger data-testid={`request-status-select-${r.id}`} className="h-9 rounded-full text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {REQ_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
}

function CorrectionsPanel() {  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/institution-corrections", { params: { state: "open" } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const resolve = async (id, action, field_value = null) => {
    try { await api.post(`/admin/institution-corrections/${id}/resolve`, { action, field_value }); toast.success(action === "apply" ? "Applied" : "Dismissed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  if (items === null) return <p className="text-muted-foreground">Loading corrections…</p>;
  const applicable = ["name", "location", "website", "founded", "type"];
  return (
    <div data-testid="corrections-panel">
      <h3 className="mb-2 font-heading text-lg font-medium">Correction requests <span className="text-sm font-normal text-muted-foreground">({items.length} open)</span></h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No open correction requests.</p> : (
        <div className="space-y-2">
          {items.map((c) => (
            <Card key={c.id} className="border-border/60 p-4" data-testid={`correction-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <a href={`/institutions/${c.institution_id}`} target="_blank" rel="noreferrer" className="font-medium text-secondary hover:underline">{c.institution_name}</a>
                    <Badge variant="outline" className="rounded-full text-xs font-normal capitalize">{c.field}</Badge>
                  </div>
                  {c.suggested_value && <p className="mt-1 text-sm">Suggested: <strong>{c.suggested_value}</strong></p>}
                  {c.note && <p className="mt-0.5 text-sm text-muted-foreground">{c.note}</p>}
                  <p className="mt-1 text-xs text-muted-foreground">By {c.reporter_name || "anonymous"}{c.contact ? ` · ${c.contact}` : ""}</p>
                </div>
                <div className="flex gap-2">
                  {applicable.includes(c.field) && c.suggested_value && (
                    <Button size="sm" className="rounded-full" onClick={() => resolve(c.id, "apply")} data-testid={`correction-apply-${c.id}`}>Apply</Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => resolve(c.id, "dismiss")} data-testid={`correction-dismiss-${c.id}`}>Dismiss</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DuplicatesTab() {
  const [data, setData] = useState(null);
  const [keep, setKeep] = useState({});
  const [busy, setBusy] = useState(null);
  const load = () => { setData(null); api.get("/admin/institutions/duplicates").then((r) => { setData(r.data); const k = {}; (r.data.clusters || []).forEach((c, i) => { k[i] = c[0].id; }); setKeep(k); }).catch(() => setData(false)); };
  useEffect(() => { load(); }, []);
  const merge = async (ci, cluster) => {
    const keepId = keep[ci];
    const mergeIds = cluster.filter((x) => x.id !== keepId).map((x) => x.id);
    if (!mergeIds.length) return;
    setBusy(ci);
    try {
      const { data: res } = await api.post("/admin/institutions/merge", { keep_id: keepId, merge_ids: mergeIds });
      toast.success(`Merged ${res.merged} duplicate${res.merged === 1 ? "" : "s"} into the kept record`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
    finally { setBusy(null); }
  };
  if (data === null) return <p className="text-muted-foreground">Scanning for duplicates…</p>;
  if (data === false) return <p className="text-muted-foreground">Could not load duplicates.</p>;
  if (!data.clusters.length) return <p className="text-muted-foreground" data-testid="dupes-empty">No near-duplicate institutions found. 🎉</p>;
  return (
    <div className="space-y-4" data-testid="admin-duplicates-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{data.total_clusters} possible duplicate group{data.total_clusters === 1 ? "" : "s"} found. Pick the record to keep — the others merge into it (members, groups and links move over).</p>
        <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={load} data-testid="dupes-refresh"><ArrowsClockwise size={14} /> Rescan</Button>
      </div>
      {data.clusters.map((cluster, ci) => (
        <Card key={ci} className="border-border/60 p-4" data-testid={`dupe-cluster-${ci}`}>
          <div className="space-y-2">
            {cluster.map((it) => (
              <label key={it.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${keep[ci] === it.id ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-slate-300"}`}>
                <input type="radio" name={`keep-${ci}`} checked={keep[ci] === it.id} onChange={() => setKeep((k) => ({ ...k, [ci]: it.id }))} data-testid={`dupe-keep-${it.id}`} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground">{it.name}</p>
                  <p className="text-xs text-muted-foreground">{[it.type, it.location, it.country].filter(Boolean).join(" · ")}{it.source ? ` · ${it.source}` : ""}</p>
                </div>
                {keep[ci] === it.id && <Badge className="rounded-full bg-green-100 text-xs font-normal text-green-800">Keep</Badge>}
              </label>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" className="rounded-full gap-1.5" disabled={busy === ci} onClick={() => merge(ci, cluster)} data-testid={`dupe-merge-${ci}`}>
              {busy === ci ? "Merging…" : `Merge ${cluster.length - 1} into kept`}
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function WhatsAppTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("awaiting");
  const load = () => { setData(null); api.get("/admin/groups/whatsapp").then((r) => setData(r.data)).catch(() => setData(false)); };
  useEffect(() => { load(); }, []);
  if (data === null) return <p className="text-muted-foreground">Loading…</p>;
  if (data === false) return <p className="text-muted-foreground">Could not load WhatsApp overview.</p>;
  const { groups, threshold, summary } = data;
  const shown = groups.filter((g) => {
    if (filter === "ready") return !g.connected && g.interest_count >= threshold;
    if (filter === "awaiting") return !g.connected && g.interest_count > 0;
    if (filter === "connected") return g.connected;
    return true;
  });
  const statusPill = (g) => {
    if (g.connected) return <Badge className="rounded-full bg-green-100 text-xs font-normal text-green-800">Connected{g.status ? ` · ${g.status}` : ""}</Badge>;
    if (g.interest_count >= threshold) return <Badge className="rounded-full bg-amber-100 text-xs font-normal text-amber-800">Ready to set up</Badge>;
    return <Badge variant="outline" className="rounded-full text-xs font-normal text-muted-foreground">No group yet</Badge>;
  };
  return (
    <div className="space-y-4" data-testid="admin-whatsapp-tab">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi icon={UsersThree} label="Groups" value={summary.total} testid="wa-kpi-total" />
        <Kpi icon={WhatsappLogo} label="Connected" value={summary.connected} testid="wa-kpi-connected" />
        <Kpi icon={TrendUp} label="Awaiting interest" value={summary.awaiting} testid="wa-kpi-awaiting" />
        <Kpi icon={Warning} label={`Ready (≥${threshold})`} value={summary.ready} sub="nudge managers" testid="wa-kpi-ready" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {[["ready", `Ready to set up (≥${threshold})`], ["awaiting", "Has interest"], ["connected", "Connected"], ["all", "All"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`wa-filter-${v}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === v ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{l}</button>
        ))}
        <Button size="sm" variant="outline" className="ml-auto rounded-full gap-1.5" onClick={load} data-testid="wa-refresh"><ArrowsClockwise size={14} /> Refresh</Button>
      </div>
      {shown.length === 0 ? <p className="text-muted-foreground">No groups in this view.</p> : (
        <div className="space-y-2">
          {shown.map((g) => (
            <Card key={g.id} className="border-border/60 p-4" data-testid={`wa-group-${g.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <a href={`/groups/${g.id}`} target="_blank" rel="noreferrer" className="font-medium text-secondary hover:underline">{g.name}</a>
                    {statusPill(g)}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{[g.institution, `${g.member_count} members`].filter(Boolean).join(" · ")}{g.nudged_at ? " · manager nudged" : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-heading text-lg font-semibold">{g.interest_count}</div>
                    <div className="text-[10px] text-muted-foreground">interested</div>
                  </div>
                  <a href={`/groups/${g.id}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-secondary" data-testid={`wa-open-${g.id}`}><ArrowUpRight size={16} /></a>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const REASON_LABELS = { spam: "Spam", abuse: "Abuse / harassment", hate: "Hate speech", nsfw: "Explicit content", misinfo: "Misinformation", other: "Other" };

function ModerationTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => { setItems(null); api.get("/admin/moderation/reports").then((r) => setItems(r.data)).catch(() => setItems([])); };
  useEffect(() => { load(); }, []);
  const act = async (postId, path, ok) => {
    setBusy(postId + path);
    try { await api.post(`/admin/moderation/posts/${postId}/${path}`); toast.success(ok); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(""); }
  };
  const warn = async (postId) => {
    const message = window.prompt("Message to send the author (leave blank for the default warning):") ?? null;
    if (message === null) return;
    setBusy(postId + "warn");
    try { await api.post(`/admin/moderation/posts/${postId}/warn`, { message }); toast.success("Author warned"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };
  if (items === null) return <p className="text-muted-foreground">Loading reports…</p>;
  return (
    <div className="space-y-4" data-testid="moderation-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length === 0 ? "Nothing reported. All clear. 🎉" : `${items.length} reported ${items.length === 1 ? "post" : "posts"} awaiting review.`}</p>
        <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={load} data-testid="moderation-refresh"><ArrowsClockwise size={14} /> Refresh</Button>
      </div>
      {items.map(({ post, reports, report_count }) => (
        <Card key={post.id} className="border-border/60 p-4" data-testid={`report-${post.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="rounded-full bg-red-100 text-xs text-red-800">{report_count} report{report_count > 1 ? "s" : ""}</Badge>
                <span className="text-xs text-muted-foreground">by {post.author_name || "—"}{post.status !== "active" ? ` · ${post.status}` : ""}</span>
              </div>
              {post.title && <p className="mt-1.5 font-medium">{post.title}</p>}
              <p className="mt-0.5 line-clamp-3 whitespace-pre-line text-sm text-muted-foreground">{post.body || "(no text — media/attachment post)"}</p>
              <div className="mt-2 space-y-1">
                {reports.map((r, i) => (
                  <p key={i} className="text-xs text-muted-foreground"><span className="font-medium text-foreground/80">{REASON_LABELS[r.reason] || r.reason}</span>{r.note ? ` — "${r.note}"` : ""} · reported by {r.reporter_name}</p>
                ))}
              </div>
            </div>
            <a href={`/${post.entity_type === "group" ? "groups" : post.entity_type === "institution" ? "institutions" : post.entity_type + "s"}/${post.entity_id}`} target="_blank" rel="noreferrer" className="shrink-0 text-muted-foreground hover:text-secondary" data-testid={`report-open-${post.id}`}><ArrowUpRight size={16} /></a>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="destructive" className="h-8 rounded-full text-xs" disabled={!!busy} onClick={() => window.confirm("Remove this post and all its comments from the site?") && act(post.id, "remove", "Post removed")} data-testid={`report-remove-${post.id}`}>Remove post</Button>
            <Button size="sm" variant="outline" className="h-8 rounded-full text-xs gap-1" disabled={!!busy} onClick={() => warn(post.id)} data-testid={`report-warn-${post.id}`}><Megaphone size={13} /> Warn author</Button>
            <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs text-muted-foreground gap-1" disabled={!!busy} onClick={() => act(post.id, "dismiss", "Reports dismissed")} data-testid={`report-dismiss-${post.id}`}><CheckCircle size={13} /> Dismiss</Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

const ROLE_LABELS = { user: "Member", moderator: "Moderator", admin: "Admin" };

function UsersTab({ isAdmin, selfId }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const reqId = useRef(0);
  const load = (query = q) => {
    const id = ++reqId.current;
    setItems(null);
    api.get("/admin/users", { params: { q: query, limit: 100 } })
      .then((r) => { if (id === reqId.current) setItems(r.data); })
      .catch(() => { if (id === reqId.current) setItems([]); });
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);
  const ban = async (u) => {
    const hide = window.confirm(`Ban ${u.name}?\n\nOK = also HIDE all their posts, comments & profile.\nCancel this dialog to choose block-login-only next.`);
    let hide_content = hide;
    if (!hide) { if (!window.confirm(`Ban ${u.name} (block login only, keep their content visible)?`)) return; hide_content = false; }
    const reason = window.prompt("Reason (optional, internal):") || "";
    setBusy(u.id);
    try { const { data } = await api.post(`/admin/users/${u.id}/ban`, { hide_content, reason }); toast.success(hide_content ? `Banned · ${data.items_hidden} items hidden` : "Banned (login blocked)"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };
  const unban = async (u) => {
    setBusy(u.id);
    try { await api.post(`/admin/users/${u.id}/unban`); toast.success("Unbanned & content restored"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };
  const setRole = async (u, role) => {
    setBusy(u.id);
    try { await api.post(`/admin/users/${u.id}/role`, { role }); toast.success(`Role set to ${ROLE_LABELS[role]}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };
  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search members by name or email…" className="rounded-full pl-10" data-testid="users-search" />
        </div>
        <Button className="rounded-full" onClick={() => load()} data-testid="users-search-btn">Search</Button>
      </div>
      {items === null ? <p className="text-muted-foreground">Loading members…</p>
        : items.length === 0 ? <p className="text-muted-foreground">No members found.</p>
          : <div className="space-y-2">
            {items.map((u) => (
              <Card key={u.id} className={`border-border/60 p-4 ${u.banned ? "bg-red-50/40" : ""}`} data-testid={`user-${u.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{u.name}</span>
                      <Badge variant="outline" className="rounded-full text-[10px] font-normal capitalize">{ROLE_LABELS[u.role] || u.role}</Badge>
                      {u.banned && <Badge className="rounded-full bg-red-100 text-[10px] font-normal text-red-800" data-testid={`user-banned-${u.id}`}>Banned</Badge>}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{u.email}{u.institution ? ` · ${u.institution}` : ""}{u.created_at ? ` · joined ${new Date(u.created_at).toLocaleDateString()}` : ""}</p>
                    {u.banned && u.ban_reason && <p className="mt-0.5 text-xs italic text-red-700/80">Reason: {u.ban_reason}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {isAdmin && u.id !== selfId && u.role !== "admin" && (
                      <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">Member</SelectItem>
                          <SelectItem value="moderator">Moderator</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {u.role !== "admin" && (u.banned
                      ? <Button size="sm" variant="outline" className="h-8 rounded-full text-xs gap-1" disabled={busy === u.id} onClick={() => unban(u)} data-testid={`unban-${u.id}`}><CheckCircle size={13} /> Unban</Button>
                      : <Button size="sm" variant="destructive" className="h-8 rounded-full text-xs gap-1" disabled={busy === u.id} onClick={() => ban(u)} data-testid={`ban-${u.id}`}><Prohibit size={13} /> Ban</Button>)}
                  </div>
                </div>
              </Card>
            ))}
          </div>}
    </div>
  );
}

const INST_TYPES = ["School", "College", "University", "Institute", "Coaching", "Other"];

function InstitutionEditTab() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = () => { if (!q.trim()) return; setResults(null); api.get("/institutions", { params: { q: q.trim(), limit: 20 } }).then((r) => setResults(r.data.results || r.data.items || [])).catch(() => setResults([])); };
  const pick = async (inst) => {
    try { const { data } = await api.get(`/institutions/${inst.id}`); setSel(data); setForm({ name: data.name || "", type: data.type || "", location: data.location || "", city: data.city || "", state: data.state || "", country: data.country || "", website: data.website || "", description: data.description || data.about || "", verified: !!data.verified }); }
    catch { toast.error("Could not load institution"); }
  };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/admin/institutions/${sel.id}`, form); setSel(data); toast.success("Institution updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  const togglePublish = async (published) => {
    try { await api.post(`/admin/institutions/${sel.id}/publish`, { published }); setSel({ ...sel, site_published: published }); toast.success(published ? "Site published" : "Site unpublished"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="inst-edit-tab">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search any institution to edit…" className="rounded-full pl-10" data-testid="inst-edit-search" />
        </div>
        <Button className="rounded-full" onClick={search} data-testid="inst-edit-search-btn">Search</Button>
      </div>
      {results !== null && !sel && (
        results.length === 0 ? <p className="text-muted-foreground">No institutions found.</p>
          : <div className="space-y-2">{results.map((r) => (
            <button key={r.id} onClick={() => pick(r)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-border/60 p-3 text-left transition-colors hover:border-secondary/50" data-testid={`inst-result-${r.id}`}>
              <div className="min-w-0"><span className="block truncate font-medium">{r.name}</span><span className="text-xs text-muted-foreground">{[r.type, r.location || r.city, r.state, r.country].filter(Boolean).join(" · ")}</span></div>
              <PencilSimple size={16} className="shrink-0 text-muted-foreground" />
            </button>
          ))}</div>
      )}
      {sel && form && (
        <Card className="border-border/60 p-5" data-testid="inst-edit-form">
          <div className="mb-4 flex items-center justify-between">
            <button onClick={() => { setSel(null); setForm(null); }} className="text-sm text-secondary hover:underline" data-testid="inst-edit-back">← Back to results</button>
            <a href={`/institutions/${sel.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-secondary">View page <ArrowUpRight size={13} /></a>
          </div>
          <div className="space-y-3">
            <div><Label>Name</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inst-edit-name" /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1" data-testid="inst-edit-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>{INST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Website</Label><Input className="mt-1" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" data-testid="inst-edit-website" /></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div><Label>City</Label><Input className="mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>State</Label><Input className="mt-1" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div><Label>Country</Label><Input className="mt-1" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
            </div>
            <div><Label>Description</Label><Textarea rows={4} className="mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="inst-edit-description" /></div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} data-testid="inst-edit-verified" />
              <span>Verified institution</span>
            </label>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button onClick={save} disabled={busy} className="rounded-full gap-1.5" data-testid="inst-edit-save"><CheckCircle size={15} /> {busy ? "Saving…" : "Save changes"}</Button>
              {sel.site_published
                ? <Button variant="outline" className="rounded-full" onClick={() => togglePublish(false)} data-testid="inst-unpublish">Unpublish site</Button>
                : <Button variant="outline" className="rounded-full" onClick={() => togglePublish(true)} data-testid="inst-publish">Publish site</Button>}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
